import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  Context,
} from "react";
import { ReportConfig, STYLE_CONSTANTS, ReportElementSizes } from "./config";
import {
  FilterOnChange,
  ReportFilterFields,
  ReportFilterValues,
} from "./filters/config/types";
import { useDispatch } from "react-redux";
import { oauth } from "../../utils";
import { mapSummaryDataByConfig, ReportSummaryData } from "./summary/types";
import {
  mapRowDataByConfig,
  ReportRow,
} from "./columns/config/ReportColumnConfig";
import { ReportTypeValues } from "./routes/report_types";
import { rebuildTooltip } from "../helpers";
import { ReportFilterConfig } from "./filters/config/ReportFilterConfig";
import SubReportPopup from "./sub-report/SubReportPopup";

interface IReportContext {
  reportConfig: ReportConfig;
  columns: ReportConfig["columns"];
  query: {};
  prevQuery: {};
  onFilterChange: FilterOnChange;
  fetchReport: () => void;
  summaryData?: ReportSummaryData[];
  rowData: ReportRow[];
  activeRow?: ReportRow;
  loading: boolean;
  firstLoaded: boolean;
  tableHeight: number;
  sort: Sort;
  pageNo: number;
  pageSize: number;
  setPageNumber: (page: number) => void;
  totalRows: number;
  sortByColumn: (orderBy: string, orderDir: string) => void;
  getRawQuery: (extraOptions?: {}) => Record<string, string>;
  selectedRows?: Record<string, unknown>[];
  onSelectionChange?: (rows: Record<string, unknown>[]) => void;
  onClearSelection: () => void;
  setActiveRow: (activeRow: ReportRow) => void;
  setSubReportType: (subReportType: ReportTypeValues) => void;
  resetQuery: (filers?: ReportFilterConfig[]) => void;
  elementSizeParameters: ReportElementSizes;
  selectedRowsOverall?: string[];
  listenToClickOutside: boolean;
  setListenToClickOutside: (listen: boolean) => void;
}

interface Sort {
  orderBy: string;
  orderDir: string;
}

interface FetchReportResponse {
  json: {};
  response: Response;
}

const DEFAULT_PAGE_SIZE = 50;

const ReportContext: Context<IReportContext> =
  createContext<IReportContext>(null);

interface ReportProviderProps {
  reportConfig: ReportConfig;
  defaultFilters?: {};
  elementSizeParameters?: ReportElementSizes;
  containerHeight?: number;
  children: React.ReactNode;
}

/**
 * Filters out entries from the provided query object where the value is falsy.
 * @param query - An object containing key-value pairs to be filtered.
 * @returns A new object containing only the entries from `query` whose values are truthy.
 */
function getBaseQuery(query: Record<string, any>) {
  return Object.entries(query).reduce(
    (acc, [key, value]) => (value ? { ...acc, [key]: value } : acc),
    {}
  );
}

export const ReportProvider = ({
  reportConfig,
  defaultFilters,
  elementSizeParameters = STYLE_CONSTANTS,
  containerHeight,
  children,
}: ReportProviderProps) => {
  const dispatch = useDispatch();

  const [query, setQuery] = useState(
    () => defaultFilters ?? reportConfig?.filters?.initializeReportQuery()
  );

  const [listenToClickOutside, setListenToClickOutside] =
    useState<boolean>(true);

  const [prevQuery, setPrevQuery] = useState(() => ({}));
  const [selectedRowsOverall, setSelectedRowsOverall] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<Record<string, unknown>[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [firstLoaded, setFirstLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [summaryData, setSummaryData] = useState<ReportSummaryData[]>([]);
  const [rowData, setRowData] = useState<ReportRow[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const pageSize = reportConfig?.resultsPerPage || DEFAULT_PAGE_SIZE;
  const [totalRows, setTotalRows] = useState<number>(0);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const abortController = useRef<AbortController>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [sort, setSort] = useState<Sort>({
    orderBy: "",
    orderDir: "",
  });
  const [subReportType, setSubReportType] = useState<
    ReportTypeValues | undefined
  >();
  const [activeRow, setActiveRow] = useState<ReportRow | undefined>();

  const fitableRows = useMemo(() => {
    let height =
      containerHeight -
      elementSizeParameters.TABLE_PAGE_HEADER_HEIGHT -
      elementSizeParameters.TABLE_BOTTOM_MARGIN -
      elementSizeParameters.TABLE_ELEMENTS_GAP * 2;
    if (reportConfig.summary?.length > 0) {
      height -= elementSizeParameters.SUMMARY_CONTAINER_HEIGHT;
    }
    if (
      reportConfig.withSelection &&
      reportConfig.bulkActions &&
      firstLoaded &&
      !loading &&
      totalRows > 0
    ) {
      height -= elementSizeParameters.BULK_ACTION_BAR_HEIGHT;
    }

    return {
      numberOfRows: Math.floor(height / elementSizeParameters.ROW_HEIGHT),
      height,
    };
  }, [reportConfig.summary?.length, containerHeight, elementSizeParameters]);

  useEffect(() => {
    const rowsInPage = rowData.length;
    const { numberOfRows, height } = fitableRows;

    let tableHeight: number;

    if (rowsInPage < numberOfRows) {
      tableHeight = height;
    } else {
      tableHeight = rowsInPage * elementSizeParameters.ROW_HEIGHT;
    }

    setTableHeight(tableHeight);
    rebuildTooltip();
  }, [
    elementSizeParameters.ROW_HEIGHT,
    fitableRows,
    pageSize,
    reportConfig.summary?.length,
    rowData.length,
  ]);

  const handleScroll = useCallback(() => {
    if (
      reportConfig.scrollOnFilterChange ||
      reportConfig.scrollOnFilterChange === undefined
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [reportConfig.scrollOnFilterChange]);

  const getRawQuery = useCallback(() => getBaseQuery(query), [query]);

  /**
   * Builds the full query object for fetching report data, including pagination and sorting.
   * @param query - The base query object containing filter values.
   * @param page - The current page number (0-indexed).
   * @param orderBy - The column name to sort by.
   * @param orderDir - The sort direction ("asc" or "desc").
   * @returns The complete query object with pagination and sorting parameters.
   */
  const getQuery = useCallback(
    (query: {}, page: number = 0, orderBy?: string, orderDir?: string) => {
      const baseQuery = getBaseQuery(query);

      const fullQuery = {
        ...baseQuery,
        "max-results": pageSize,
        "start-index": page > 0 ? page * pageSize : 0,
        ...(orderBy ? { order_by: orderBy } : {}),
        ...(orderDir ? { order_dir: orderDir } : {}),
      };

      return fullQuery;
    },
    [pageSize]
  );

  const baseFetchReport = useCallback(
    async (reportQuery: {}) => {
      const errorHandler = (
        e?: Error | string,
        stopLoading: boolean = true
      ) => {
        e && console.log("Error fetching report", e);
        stopLoading && setLoading(false);
        setError("Error fetching report");
      };

      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();

      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }

      setLoading(true);
      setSelectedRows([]);
      setActiveRow(undefined);

      let res: FetchReportResponse;

      try {
        res = await oauth(
          reportConfig.usePost ? "POST" : "GET",
          reportConfig.endpoint,
          reportQuery,
          undefined,
          { signal: abortController.current.signal }
        );
      } catch (e) {
        errorHandler(e, e.name !== "AbortError");
        return;
      }

      if (!res.json) {
        errorHandler("Empty response");
        return;
      }

      const { json } = res;

      setPrevQuery(reportQuery);
      reportConfig?.summary?.length > 0 &&
        setSummaryData(() =>
          mapSummaryDataByConfig(
            json[reportConfig.summaryKey],
            reportConfig.summary
          )
        );
      const rowData = mapRowDataByConfig(
        json[reportConfig.reportKey],
        reportConfig.columns
      );
      setRowData(rowData);
      setTotalRows(
        parseInt(json[reportConfig.summaryKey]?.row_count as string) || 0
      );
      setFirstLoaded(true);
      setLoading(false);

      // Wait for DOM to stabilize before rebuilding tooltips
      tooltipTimeoutRef.current = setTimeout(() => {
        rebuildTooltip();
        tooltipTimeoutRef.current = null;
      }, 100);

      // set rows with selected rows
      if (
        !reportConfig.disableMultiPageSelect &&
        selectedRowsOverall.length > 0
      ) {
        // Currently selected rows
        let selectedRows = rowData.filter((r) =>
          selectedRowsOverall.includes(JSON.stringify(r))
        );
        setSelectedRows(selectedRows);
      }
    },
    [
      reportConfig.summaryKey,
      reportConfig.endpoint,
      reportConfig.summary,
      reportConfig.reportKey,
      reportConfig.columns,
      reportConfig.disableMultiPageSelect,
      selectedRowsOverall,
    ]
  );

  const fetchReport = useCallback(async () => {
    setPageNo(0);
    const reportQuery = getQuery(query, 0, sort.orderBy, sort.orderDir);
    await baseFetchReport(reportQuery);
    if (!reportConfig.disableMultiPageSelect) {
      setSelectedRowsOverall([]);
    }
  }, [
    getQuery,
    query,
    sort.orderBy,
    sort.orderDir,
    baseFetchReport,
    reportConfig.disableMultiPageSelect,
  ]);

  useEffect(() => {
    if (defaultFilters) {
      fetchReport();
    }
  }, [defaultFilters]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
    };
  }, []);

  const refetchOnFilterChange = useCallback(
    async (query) => {
      try {
        const reportQuery = getQuery(
          query,
          pageNo,
          sort.orderBy,
          sort.orderDir
        );
        await baseFetchReport(reportQuery);
      } catch (e) {
        console.log("Error fetching report", e);
      }
    },
    [pageNo, sort.orderBy, sort.orderDir, baseFetchReport]
  );

  const onFilterChange: FilterOnChange = useCallback(
    async (fields: ReportFilterFields, values: ReportFilterValues) => {
      const newQuery =
        typeof fields === "string"
          ? { ...query, [fields]: values }
          : Array.isArray(fields)
          ? fields.reduce(
              (acc, field, index) => ({
                ...acc,
                [field]: values[index],
              }),
              { ...query }
            )
          : query;
      setQuery(newQuery);

      const dependentFields = reportConfig.filters.dependentFields;
      let hasDependentFields: boolean;
      if (Array.isArray(fields)) {
        hasDependentFields = fields.some((field) => dependentFields.has(field));
      } else {
        hasDependentFields = dependentFields.has(fields);
      }
      if (hasDependentFields) {
        const dependentQuery = Array.from(dependentFields).reduce(
          (acc, field) => ({
            ...acc,
            ...(query[field] !== newQuery[field]
              ? { [field]: newQuery[field] }
              : {}),
          }),
          {}
        );
        reportConfig.filters.loadDataForDependentFilters(
          dependentQuery,
          dispatch
        );
      }

      if (reportConfig.refetchOnFilterChange) {
        const reportQuery = getQuery(newQuery, 0, sort.orderBy, sort.orderDir);
        setPageNo(0);
        setSelectedRowsOverall([]);
        await baseFetchReport(reportQuery).catch(console.log);
        handleScroll();
      }
    },
    [
      dispatch,
      reportConfig.filters,
      reportConfig.refetchOnFilterChange,
      baseFetchReport,
      query,
      getQuery,
      handleScroll,
      sort.orderBy,
      sort.orderDir,
    ]
  );

  const setPageNumber = useCallback(
    async (page: number) => {
      if (page !== pageNo) {
        const reportQuery = getQuery(query, page, sort.orderBy, sort.orderDir);
        setPageNo(page);
        await baseFetchReport(reportQuery).catch(console.log);
        handleScroll();
      }
    },
    [
      getQuery,
      baseFetchReport,
      handleScroll,
      pageNo,
      sort.orderBy,
      sort.orderDir,
      query,
    ]
  );

  const sortByColumn = useCallback(
    async (orderBy: string, orderDir: string) => {
      const reportQuery = getQuery(query, 0, orderBy, orderDir);
      setPageNo(0);
      setSort({ orderBy, orderDir });
      await baseFetchReport(reportQuery).catch(console.log);
      handleScroll();
    },
    [query, getQuery, baseFetchReport, handleScroll]
  );

  const onSelectionChange = useCallback(
    (rows: Record<string, unknown>[]) => {
      setSelectedRows(rows);
      rebuildTooltip();

      let rowsToDelete = [];
      let rowsToAdd = [];
      let rowsSelectedInPage = rows.map((r) => JSON.stringify(r));

      let currentRowsInDisplay = rowData;
      currentRowsInDisplay.forEach((currentRow) => {
        if (!rowsSelectedInPage.includes(JSON.stringify(currentRow))) {
          rowsToDelete.push(JSON.stringify(currentRow));
        } else {
          rowsToAdd.push(JSON.stringify(currentRow));
        }
      });
      let newRowsAll = selectedRowsOverall;

      // Remove rows that are no longer selected on this page
      newRowsAll = newRowsAll.filter((r) => !rowsToDelete.includes(r));

      // Add rows that needs to be added on this page
      rowsToAdd.forEach((n) => {
        if (!newRowsAll.includes(n)) {
          newRowsAll.push(n);
        }
      });
      setSelectedRowsOverall(newRowsAll);
    },
    [rowData, selectedRowsOverall]
  );

  const onClearSelection = useCallback(() => {
    setSelectedRows([]);
    rebuildTooltip();
  }, []);

  const resetQuery = useCallback(
    (filters?: ReportFilterConfig[]) => {
      setPrevQuery({});
      const newQuery = {
        ...prevQuery,
        ...reportConfig.filters.initializeReportQuery(filters, false),
      };

      setQuery(newQuery);

      if (reportConfig.refetchOnFilterChange) {
        refetchOnFilterChange(newQuery);
      }
    },
    [reportConfig.filters, refetchOnFilterChange]
  );

  const columns = useMemo(
    () =>
      reportConfig.columns.filter(
        (c) => !c.isVisible || c.isVisible(prevQuery)
      ),
    [reportConfig.columns, prevQuery]
  );

  const value: IReportContext = useMemo(
    () => ({
      reportConfig,
      columns,
      query,
      prevQuery,
      onFilterChange,
      fetchReport,
      summaryData,
      rowData,
      activeRow,
      loading,
      firstLoaded,
      tableHeight,
      pageNo,
      pageSize,
      setPageNumber,
      totalRows,
      sortByColumn,
      getRawQuery,
      selectedRows,
      onSelectionChange,
      onClearSelection,
      resetQuery,
      setActiveRow,
      setSubReportType,
      sort,
      elementSizeParameters,
      selectedRowsOverall,
      listenToClickOutside,
      setListenToClickOutside,
    }),
    [
      reportConfig,
      columns,
      query,
      prevQuery,
      onFilterChange,
      fetchReport,
      summaryData,
      rowData,
      activeRow,
      loading,
      firstLoaded,
      tableHeight,
      pageNo,
      pageSize,
      setPageNumber,
      totalRows,
      sortByColumn,
      getRawQuery,
      selectedRows,
      onSelectionChange,
      onClearSelection,
      resetQuery,
      sort,
      elementSizeParameters,
      selectedRowsOverall,
      listenToClickOutside,
      setListenToClickOutside,
    ]
  );

  const subReport = useMemo(() => {
    if (!subReportType || !activeRow) {
      return null;
    }
    const popupTitle = reportConfig.subReports.getTitle(activeRow);

    return (
      <SubReportPopup
        title={popupTitle}
        subReportType={subReportType}
        onClose={() => {
          setSubReportType(undefined);
          setActiveRow(undefined);
        }}
      />
    );
  }, [reportConfig.subReports, subReportType, activeRow]);

  return (
    <ReportContext.Provider value={value}>
      {subReport}
      {children}
    </ReportContext.Provider>
  );
};

export const useReportContext = (): IReportContext => {
  const context = useContext(ReportContext);

  if (!context) {
    throw new Error("useReportContext must be used within a ReportProvider");
  }

  return context;
};
